/**
 * Plan mapping utility to standardize plan names and codes
 * This ensures consistent plan naming across the frontend
 */

// Standardized plan codes and names
export const STANDARD_PLANS = {
  FREE: {
    planCode: 'FREE',
    name: 'Free',
    description: 'Free tier with 4 hours storage',
    type: 'TRIAL',
    storageHours: 4,
    freePurchaseSecondsPerMonth: 0
  },
  ELITE: {
    planCode: 'ELITE', 
    name: 'Elite',
    description: '8 hours storage',
    type: 'BASIC',
    storageHours: 8,
    freePurchaseSecondsPerMonth: 0
  },
  PRO: {
    planCode: 'PRO',
    name: 'Pro', 
    description: '16 hours storage + 1h monthly credit',
    type: 'STANDARD',
    storageHours: 16,
    freePurchaseSecondsPerMonth: 3600 // 1 hour in seconds
  },
  GOAT: {
    planCode: 'GOAT',
    name: 'Goat',
    description: '100 hours storage + 3h monthly credit', 
    type: 'PRO',
    storageHours: 100,
    freePurchaseSecondsPerMonth: 10800 // 3 hours in seconds
  }
};

// Legacy plan code mappings to standard codes
export const LEGACY_PLAN_MAPPING = {
  'TIER_1_MONTHLY': 'ELITE',
  'TIER_2_MONTHLY': 'PRO', 
  'TIER_3_MONTHLY': 'GOAT',
  'FREE': 'FREE'
};

// Quota-based plan inference mapping
export const QUOTA_PLAN_MAPPING = {
  14400: 'FREE',    // 4 hours
  28800: 'ELITE',   // 8 hours  
  57600: 'PRO',     // 16 hours (updated to match actual Pro plan)
  360000: 'GOAT'    // 100 hours
};

/**
 * Normalize a plan object to use standard naming
 * @param {Object} plan - The plan object from backend
 * @returns {Object} - Normalized plan object
 */
export const normalizePlan = (plan) => {
  if (!plan) return null;
  
  // Map legacy plan codes to standard codes
  const standardPlanCode = LEGACY_PLAN_MAPPING[plan.planCode] || plan.planCode;
  const standardPlan = STANDARD_PLANS[standardPlanCode];
  
  if (standardPlan) {
    return {
      ...plan,
      planCode: standardPlan.planCode,
      name: standardPlan.name,
      description: standardPlan.description,
      type: standardPlan.type,
      storageHours: standardPlan.storageHours
    };
  }
  
  return plan;
};

/**
 * Get plan name from quota limit
 * @param {number} limitSeconds - Quota limit in seconds
 * @returns {string} - Plan name
 */
export const getPlanNameFromQuota = (limitSeconds) => {
  const planCode = QUOTA_PLAN_MAPPING[limitSeconds];
  if (planCode && STANDARD_PLANS[planCode]) {
    return STANDARD_PLANS[planCode].name;
  }
  
  // Fallback logic for ranges
  if (limitSeconds <= 14400) return 'Free';
  if (limitSeconds <= 28800) return 'Elite';
  if (limitSeconds <= 57600) return 'Pro';  // Updated to 16 hours
  if (limitSeconds >= 360000) return 'Goat';
  
  return 'Unknown Plan';
};

/**
 * Get plan from subscription data with fallback to quota inference
 * @param {Object} subscription - Subscription object
 * @param {Object} quota - Quota object  
 * @param {Array} plans - Available plans array
 * @returns {Object} - Best matching plan object
 */
export const getBestMatchingPlan = (subscription, quota, plans) => {
  if (!plans || plans.length === 0) return null;
  
  // First try to find plan from subscription
  if (subscription) {
    const subscriptionPlanCode = subscription.plan?.planCode || subscription.planCode;
    if (subscriptionPlanCode) {
      // Try exact match first
      let matchingPlan = plans.find(p => p.planCode === subscriptionPlanCode);
      
      // If no exact match, try legacy mapping
      if (!matchingPlan) {
        const standardPlanCode = LEGACY_PLAN_MAPPING[subscriptionPlanCode];
        if (standardPlanCode) {
          matchingPlan = plans.find(p => p.planCode === standardPlanCode);
        }
      }
      
      if (matchingPlan) {
        return normalizePlan(matchingPlan);
      }
    }
  }
  
  // Fallback to quota-based inference
  if (quota && quota.limitSeconds) {
    const planName = getPlanNameFromQuota(quota.limitSeconds);
    const matchingPlan = plans.find(p => p.name === planName);
    if (matchingPlan) {
      return normalizePlan(matchingPlan);
    }
  }
  
  return null;
};

/**
 * Filter plans to only show standard plans (remove duplicates)
 * @param {Array} plans - Array of plan objects
 * @returns {Array} - Filtered array with only standard plans
 */
export const filterStandardPlans = (plans) => {
  if (!plans || plans.length === 0) return [];
  
  const standardPlanCodes = Object.keys(STANDARD_PLANS);
  const filteredPlans = [];
  const seenCodes = new Set();
  
  // First pass: prioritize plans with standard codes
  plans.forEach(plan => {
    if (standardPlanCodes.includes(plan.planCode) && !seenCodes.has(plan.planCode)) {
      filteredPlans.push(normalizePlan(plan));
      seenCodes.add(plan.planCode);
    }
  });
  
  // Second pass: add legacy plans that weren't covered
  plans.forEach(plan => {
    const standardPlanCode = LEGACY_PLAN_MAPPING[plan.planCode];
    if (standardPlanCode && !seenCodes.has(standardPlanCode)) {
      filteredPlans.push(normalizePlan(plan));
      seenCodes.add(standardPlanCode);
    }
  });
  
  return filteredPlans;
};
